import { useState, useEffect, useMemo } from 'react';
import { useChatQueries } from './useChatQueries';
import { ChatCategory, Conversation, makeChatApi } from 'services/ChatApi';
import { chatTracking } from '../utils/tracking';

export enum ChatMode {
  NORMAL = 'normal',
  CATEGORIES = 'categories',
  PROMPTS = 'prompts'
}

export const organizeChats = (
  chats: Conversation[]
): {
  pinnedChats: Conversation[];
  recentChats: Conversation[];
} => {
  const pinned = chats
    .filter(chat => chat.isPinned)
    .sort((a, b) =>
      new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    );

  const recent = chats
    .filter(chat => !chat.isPinned)
    .sort((a, b) =>
      new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    );

  return {
    pinnedChats: pinned,
    recentChats: recent
  };
};

export interface IToEditProps {
  conversationId: string;
  newName: string;
  fromRHP: boolean;
}

export const useChatState = () => {
  const chatApi = makeChatApi();
  const {
    queries: { listChats, getMessages, getCategories, getPromptsByCategory, searchMessages },
    mutations
  } = useChatQueries(chatApi);

  const [activeChatId, setActiveChatId] = useState<string | null | undefined>(undefined);
  const [searchQuery, setSearchQuery] = useState('');
  const [message, setMessage] = useState('');
  const [chatMode, setChatMode] = useState<ChatMode>(ChatMode.CATEGORIES);
  const [selectedCategory, setSelectedCategory] = useState<ChatCategory | null>(null);
  const [isPromptSelected, setIsPromptSelected] = useState<boolean>(false);
  const [toDelete, setToDelete] = useState<string | null>(null);
  const [toEdit, setToEdit] = useState<IToEditProps | null>(null);

  const searchMessagesQuery = searchMessages(searchQuery);

  const { pinnedChats, recentChats } = useMemo(() => organizeChats(searchQuery ? (searchMessagesQuery.data?.data || []) : (listChats.data?.data || [])), [listChats.data, searchMessagesQuery.data, searchQuery]);
  const selectedChat = useMemo(() => [...recentChats, ...pinnedChats].find(chat => chat.conversationId === activeChatId), [activeChatId, listChats.data?.data]);
  const messagesQuery = getMessages(activeChatId || '');
  const messages = useMemo(() => messagesQuery.data?.pages.flatMap(page => page.data.messages) || [], [messagesQuery.data]);

  useEffect(() => {
    if (listChats.data?.data && listChats.data.data.length > 0 && activeChatId === undefined) {
      setActiveChatId(listChats.data.data[0].conversationId);
      setChatMode(ChatMode.NORMAL);
    }
  }, [listChats.data, activeChatId]);

  useEffect(() => {
    if (searchQuery) {
      searchMessagesQuery.refetch();
    }
  }, [searchQuery]);

  const handleSendMessage = () => {
    if (!message.trim()) return;

    if (!activeChatId) {
      mutations.sendNewMessage.mutate({ message, selectChat });
    } else {
      mutations.sendMessage.mutate({
        conversationId: activeChatId,
        message
      });
    }
    setMessage('');
  };

  const handleRenameChat = () => {
    if (activeChatId && toEdit && toEdit.conversationId.trim()) {
      const selectedChat = [...recentChats, ...pinnedChats].find(chat => chat.conversationId === activeChatId);
      if (selectedChat) {
        chatTracking.trackEditName(toEdit.conversationId, selectedChat.name, toEdit.newName);
      }
      
      mutations.renameChat.mutate({
        conversationId: toEdit.conversationId,
        newName: toEdit.newName,
        searchQuery
      });
      setToEdit(null);
    }
  };

  const handleSetChatToEdit = (editObj: IToEditProps | null) => {
    setToEdit(editObj);
  };

  const handleEditChatName = (newName: string) => {
    setToEdit(prev => prev ? ({ ...prev, newName }) : null);
  };

  const handleSelectChatToDelete = (conversationId: string | null) => {
    setToDelete(conversationId);
  };

  const handleDeleteChat = () => {
    if (!toDelete) return;
    
    const chatToDelete = [...recentChats, ...pinnedChats].find(chat => chat.conversationId === toDelete);
    if (chatToDelete) {
      chatTracking.trackDeleteChat(toDelete, chatToDelete.name);
    }
    
    mutations.deleteChat.mutate({ conversationId: toDelete, searchQuery });
    if (activeChatId === toDelete) {
      setActiveChatId(null);
      setChatMode(ChatMode.CATEGORIES);
    }
    setToDelete(null);
  };

  const handleDownloadChat = () => {
    if (activeChatId && selectedChat) {
      chatTracking.trackDownloadChat(activeChatId, selectedChat.name);
      
      mutations.downloadChat.mutate({ conversationId: activeChatId, conversationName: selectedChat.name });
    }
  };

  const handleRateMessage = (messageId: string, rating: boolean) => {
    if (activeChatId) {
      mutations.rateMessage.mutate({ conversationId: activeChatId, messageId, rating });
    }
  };

  const startNewChat = () => {
    setChatMode(ChatMode.CATEGORIES);
    setSelectedCategory(null);
    setIsPromptSelected(false);
    setActiveChatId(null);
  };

  const selectCategory = (category: ChatCategory) => {
    chatTracking.trackCategoryClick(category.categoryId, category.name);
    
    setSelectedCategory(category);
    setChatMode(ChatMode.PROMPTS);
  };

  const backToCategories = () => {
    setSelectedCategory(null);
    setChatMode(ChatMode.CATEGORIES);
  };

  const selectPrompt = (prompt: string) => {
    chatTracking.trackPromptClick(prompt, selectedCategory?.categoryId, selectedCategory?.name);
    
    setMessage(prompt);
    setIsPromptSelected(true);
    setChatMode(ChatMode.NORMAL);
    setActiveChatId(null);
  };

  const selectChat = (chatId: string) => {
    const chat = [...recentChats, ...pinnedChats].find(c => c.conversationId === chatId);
    if (chat) {
      chatTracking.trackChatSelection(chatId, chat.name);
    }
    
    setActiveChatId(chatId);
    setChatMode(ChatMode.NORMAL);
  };

  const handleSetSearchQuery = (query: string) => {
    setSearchQuery(query);
    
    if (query.trim()) {
      const searchResults = searchMessagesQuery.data?.data || [];
      chatTracking.trackSearch(query, searchResults.length);
    }
  };

  
  return {
    state: {
      message,
      chatMode,
      selectedCategory,
      isPromptSelected,
      chats: recentChats,
      pinnedChats: pinnedChats,
      activeChatId,
      messages,
      hasMoreMessages: messagesQuery.hasNextPage,
      isLoadingChats: listChats.isLoading || searchMessagesQuery.isFetching,
      isLoadingMessages: messagesQuery.isLoading,
      isSendingMessage: mutations.sendMessage.isPending || mutations.sendNewMessage.isPending,
      categories: getCategories.data?.data || [],
      prompts: getPromptsByCategory(selectedCategory ? selectedCategory.categoryId : '').data?.data || [],
      toDelete,
      toEdit,
      selectedChat,
      searchQuery,
    },
    actions: {
      setMessage,
      sendMessage: handleSendMessage,
      renameChat: handleRenameChat,
      deleteChat: handleDeleteChat,
      selectChatToDelete: handleSelectChatToDelete,
      pinChat: (pin: boolean, conversationId: string) => {
        const chat = [...recentChats, ...pinnedChats].find(c => c.conversationId === conversationId);
        if (chat) {
          chatTracking.trackPinChat(conversationId, chat.name, pin);
        }
        
        mutations.pinChat.mutate({
          conversationId,
          pin,
          searchQuery,
        });
      },
      rateMessage: handleRateMessage,
      loadMoreMessages: () => messagesQuery.fetchNextPage(),
      startNewChat,
      selectCategory,
      backToCategories,
      selectPrompt,
      selectChat,
      handleEditChatName,
      handleSetChatToEdit,
      downloadChat: handleDownloadChat,
      setSearchQuery: handleSetSearchQuery,
    }
  };
};